export const SHARE_ARTIFACT_TYPES = [
	"portfolio",
	"boq",
	"proposal",
	"contractor_quote",
	"estimate",
	"project_document",
	"design",
	"payment_request",
] as const;

export const SHARE_DELIVERY_CHANNELS = [
	"whatsapp",
	"copy_link",
	"web_share",
	"in_app",
	"sms",
	"email",
] as const;

export const SHARE_LINK_EVENTS = [
	"created",
	"opened",
	"shared",
	"copied",
	"revoked",
	"expired",
	"completed",
	// Phase 2 (WhatsApp Business / messaging providers): reserved now to avoid
	// a coordinated enum migration when a provider lands.
	"delivered",
	"read",
	"failed",
] as const;

export const SHARE_INTENDED_ACTIONS = [
	"view",
	"fund_project",
	"payment_completed",
	"review_boq",
	"view_secure_document",
	"review_proposal",
	"review_quote",
	"view_portfolio",
] as const;

export const SHARE_ACCESS_STATES = [
	"available",
	"login_required",
	"forbidden",
	"expired",
	"revoked",
	"completed",
	"unavailable",
] as const;

export type ShareArtifactType = (typeof SHARE_ARTIFACT_TYPES)[number];
export type ShareDeliveryChannel = (typeof SHARE_DELIVERY_CHANNELS)[number];
export type ShareLinkEventType = (typeof SHARE_LINK_EVENTS)[number];
export type ShareIntendedAction = (typeof SHARE_INTENDED_ACTIONS)[number];
export type ShareAccessState = (typeof SHARE_ACCESS_STATES)[number];

export interface ShareArtifactIdentity {
	type: ShareArtifactType;
	id: string;
	projectId?: string | null;
}

export interface ShareIntendedRecipient {
	name?: string;
	phone?: string;
	userId?: string;
}

export interface ShareArtifactSummary {
	type: ShareArtifactType;
	id: string;
	projectId?: string | null;
	title: string;
	description?: string | null;
	projectTitle?: string | null;
	senderRole?: "homeowner" | "designer" | "contractor" | null;
	intendedAction: ShareIntendedAction;
	targetPath?: string | null;
}

export interface ShareLinkRecord {
	id: string;
	token?: string | null;
	shareUrl?: string | null;
	artifactType: ShareArtifactType;
	artifactId: string;
	projectId?: string | null;
	senderUserId: string | null;
	deliveryChannel: ShareDeliveryChannel;
	intendedRecipient?: ShareIntendedRecipient | null;
	intendedAction: ShareIntendedAction;
	expiresAt?: string | null;
	revokedAt?: string | null;
	createdAt: string;
	updatedAt: string;
	summary: ShareArtifactSummary;
}

export interface ShareLinkEventRecord {
	id: string;
	shareLinkId: string;
	eventType: ShareLinkEventType;
	actorUserId?: string | null;
	channel?: ShareDeliveryChannel | null;
	createdAt: string;
}

export interface CreateShareLinkInput extends ShareArtifactIdentity {
	deliveryChannel?: ShareDeliveryChannel;
	intendedAction?: string;
	intendedRecipient?: ShareIntendedRecipient | null;
	expiresInDays?: number;
}

export interface CreateShareLinkResponse {
	shareLink: ShareLinkRecord;
	whatsappMessage?: string;
	whatsappUrl?: string;
}

// Gated responses (login_required, forbidden, expired, revoked, unavailable)
// intentionally do NOT include `summary` or `targetPath` to avoid leaking
// artifact/project metadata to unauthenticated/unauthorized callers.
export interface ResolveShareLinkResponse {
	accessState: ShareAccessState;
	shareLink?: Omit<ShareLinkRecord, "token">;
	summary?: ShareArtifactSummary;
	message?: string;
	requiresAuth: boolean;
	targetPath?: string | null;
}

export interface ShareHistoryResponse {
	shareLinks: Array<
		ShareLinkRecord & {
			latestEvent?: ShareLinkEventRecord | null;
		}
	>;
	nextCursor?: string | null;
}

// Client-emittable event types only. `opened` is recorded server-side on
// resolution; `completed` is server-only; created/revoked/expired/delivered/
// read/failed are infrastructure events.
export type ClientShareEventType = Extract<
	ShareLinkEventType,
	"shared" | "copied"
>;

export interface RecordShareEventInput {
	eventType: ClientShareEventType;
	channel?: ShareDeliveryChannel;
}

export function isShareArtifactType(
	value: unknown,
): value is ShareArtifactType {
	return (
		typeof value === "string" &&
		(SHARE_ARTIFACT_TYPES as readonly string[]).includes(value)
	);
}

export function isShareDeliveryChannel(
	value: unknown,
): value is ShareDeliveryChannel {
	return (
		typeof value === "string" &&
		(SHARE_DELIVERY_CHANNELS as readonly string[]).includes(value)
	);
}

export function isShareIntendedAction(
	value: unknown,
): value is ShareIntendedAction {
	return (
		typeof value === "string" &&
		(SHARE_INTENDED_ACTIONS as readonly string[]).includes(value)
	);
}

export function isShareAccessState(value: unknown): value is ShareAccessState {
	return (
		typeof value === "string" &&
		(SHARE_ACCESS_STATES as readonly string[]).includes(value)
	);
}

export function getShareArtifactLabel(type: ShareArtifactType): string {
	switch (type) {
		case "boq":
			return "BOQ";
		case "project_document":
			return "project document";
		case "design":
			return "design";
		case "payment_request":
			return "payment request";
		case "proposal":
			return "proposal";
		case "contractor_quote":
			return "contractor quote";
		case "estimate":
			return "estimate";
		case "portfolio":
			return "portfolio";
		default: {
			const _exhaustive: never = type;
			return _exhaustive;
		}
	}
}

export function buildShareMessage(
	summary: ShareArtifactSummary,
	shareUrl: string,
): string {
	const artifactLabel = getShareArtifactLabel(summary.type);
	const projectText = summary.projectTitle
		? ` for ${summary.projectTitle}`
		: "";
	const actionText =
		summary.intendedAction === "fund_project"
			? "Please review and complete the payment inside Finishd."
			: summary.intendedAction === "review_boq"
				? "Please review it inside Finishd."
				: "Please open it inside Finishd.";

	return [
		`I shared a ${artifactLabel}${projectText}: ${summary.title}`,
		actionText,
		shareUrl,
	].join("\n");
}

export function buildWhatsAppShareUrl(message: string): string {
	return `https://wa.me/?text=${encodeURIComponent(message)}`;
}
